import React, { useState, useEffect, useCallback } from 'react';
import styled, { createGlobalStyle, keyframes, css } from 'styled-components';
import { X, ArrowUpRight, Mail } from 'lucide-react';

/* ---------- theme ---------- */
const theme = {
  bg: '#0a0a0a',
  bgSecondary: '#121212',
  bgTertiary: '#161616',
  accent: '#6b1a1a',
  accentDeep: '#2d0a0a',
  textPrimary: '#e8e8e8',
  textSecondary: '#888888',
  textMuted: '#5a5a5a',
  border: '#2a2a2a',
  hover: '#8b1a1a',
};

/* ---------- global ---------- */
const GlobalStyle = createGlobalStyle`
  body {
    background: ${theme.bg};
    color: ${theme.textPrimary};
  }
  body.no-scroll { overflow: hidden; }
`;

/* ---------- animations ---------- */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const drift = keyframes`
  0%   { transform: translate(0, 0) scale(1); opacity: 0.35; }
  50%  { transform: translate(-2%, -1%) scale(1.05); opacity: 0.5; }
  100% { transform: translate(0, 0) scale(1); opacity: 0.35; }
`;

const modalIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/* ---------- layout ---------- */
const Shell = styled.div`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  isolation: isolate;
`;

const Ambience = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(ellipse 60% 40% at 20% 10%, rgba(107, 26, 26, 0.12), transparent 60%),
    radial-gradient(ellipse 50% 50% at 80% 90%, rgba(45, 10, 10, 0.25), transparent 65%),
    radial-gradient(ellipse 80% 60% at 50% 50%, rgba(20, 5, 5, 0.15), transparent 70%),
    ${theme.bg};
  animation: ${drift} 18s ease-in-out infinite;
`;

const Noise = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  opacity: 0.035;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
`;

/* ---------- header ---------- */
const Header = styled.header`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 50;
  padding: 22px clamp(20px, 4vw, 56px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  background: rgba(10, 10, 10, 0.55);
  border-bottom: 1px solid ${theme.border};
  animation: ${fadeIn} 0.8s ease-out both;
`;

const HeaderLogo = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: inherit;

  img {
    height: 22px;
    width: auto;
    filter: invert(1) brightness(1.15);
    transition: opacity 0.3s ease;
  }

  &:hover img { opacity: 0.7; }
`;

const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: ${theme.textSecondary};
  text-decoration: none;
  transition: color 0.3s ease, background 0.3s ease, transform 0.3s ease;

  &:hover {
    color: ${theme.textPrimary};
    background: rgba(139, 26, 26, 0.12);
    transform: translateY(-1px);
  }
`;

/* ---------- hero ---------- */
const Hero = styled.section`
  min-height: 100vh;
  padding: 140px clamp(20px, 4vw, 56px) 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
`;

const HeroEyebrow = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 300;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: ${theme.textMuted};
  margin-bottom: 40px;
  animation: ${fadeInUp} 0.9s 0.1s ease-out both;

  span {
    display: inline-block;
    padding: 0 14px;
    position: relative;
  }
  span::before, span::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 40px;
    height: 1px;
    background: ${theme.border};
  }
  span::before { right: 100%; }
  span::after  { left: 100%; }
`;

const HeroMark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 36px;
  animation: ${fadeInUp} 1s 0.25s ease-out both;

  img {
    height: clamp(120px, 22vw, 220px);
    width: auto;
    filter: invert(1) brightness(1.15) drop-shadow(0 0 30px rgba(139, 26, 26, 0.15));
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 300;
  font-size: clamp(28px, 4vw, 44px);
  letter-spacing: 0.02em;
  color: ${theme.textPrimary};
  margin-bottom: 18px;
  animation: ${fadeInUp} 1s 0.4s ease-out both;

  em {
    font-style: normal;
    background: linear-gradient(90deg, ${theme.textPrimary}, ${theme.hover}, ${theme.textPrimary});
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ${shimmer} 8s linear infinite;
  }
`;

const HeroTagline = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: clamp(13px, 1.3vw, 15px);
  color: ${theme.textSecondary};
  max-width: 520px;
  line-height: 1.7;
  margin-bottom: 48px;
  animation: ${fadeInUp} 1s 0.55s ease-out both;
`;

const CTAButton = styled.button`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: ${theme.textPrimary};
  background: transparent;
  border: 1px solid ${theme.border};
  padding: 18px 40px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: color 0.4s ease, border-color 0.4s ease, letter-spacing 0.4s ease;
  animation: ${fadeInUp} 1s 0.7s ease-out both;

  display: inline-flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, transparent, rgba(139, 26, 26, 0.35), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
    z-index: -1;
  }

  &:hover {
    color: #fff;
    border-color: ${theme.hover};
    letter-spacing: 0.3em;
  }
  &:hover::before { transform: translateX(100%); }

  svg { transition: transform 0.3s ease; }
  &:hover svg { transform: translate(2px, -2px); }
`;

/* ---------- socials section ---------- */
const Section = styled.section`
  padding: 100px clamp(20px, 4vw, 56px);
  border-top: 1px solid ${theme.border};
`;

const SectionHead = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 60px;
`;

const SectionLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 300;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: ${theme.textMuted};
  margin-bottom: 14px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 300;
  font-size: clamp(22px, 2.8vw, 32px);
  color: ${theme.textPrimary};
  letter-spacing: 0.01em;
`;

const SocialsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: ${theme.border};
  border: 1px solid ${theme.border};
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const SocialCard = styled.a`
  background: ${theme.bg};
  padding: 44px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-decoration: none;
  color: ${theme.textSecondary};
  transition: background 0.4s ease, color 0.4s ease;
  position: relative;
  overflow: hidden;

  .glyph {
    width: 34px;
    height: 34px;
    transition: transform 0.5s ease, color 0.4s ease;
  }

  .name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 13px;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: ${theme.textPrimary};
  }

  .handle {
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    letter-spacing: 0.15em;
    color: ${theme.textMuted};
  }

  .arrow {
    position: absolute;
    top: 18px;
    right: 18px;
    opacity: 0;
    transform: translate(-4px, 4px);
    transition: opacity 0.35s ease, transform 0.35s ease;
    color: ${theme.textMuted};
  }

  &:hover {
    background: ${theme.bgSecondary};
    color: ${theme.textPrimary};
  }
  &:hover .glyph { transform: translateY(-3px); color: ${theme.hover}; }
  &:hover .arrow { opacity: 1; transform: translate(0, 0); color: ${theme.textPrimary}; }
`;

/* ---------- footer ---------- */
const Footer = styled.footer`
  padding: 40px clamp(20px, 4vw, 56px);
  border-top: 1px solid ${theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;

  .copy {
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: ${theme.textMuted};
  }
`;

/* ---------- modal ---------- */
const modalOverlay = css`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(5, 5, 5, 0.75);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  animation: ${fadeIn} 0.35s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Overlay = styled.div`${modalOverlay}`;

const Modal = styled.div`
  background: ${theme.bgSecondary};
  border: 1px solid ${theme.border};
  max-width: 620px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${modalIn} 0.45s cubic-bezier(0.2, 0.9, 0.3, 1);
  box-shadow:
    0 0 0 1px rgba(139, 26, 26, 0.05),
    0 30px 80px rgba(0, 0, 0, 0.6),
    0 0 60px rgba(107, 26, 26, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${theme.hover}, transparent);
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${theme.border};
  color: ${theme.textSecondary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.25s ease, border-color 0.25s ease, transform 0.25s ease;

  &:hover {
    color: ${theme.textPrimary};
    border-color: ${theme.hover};
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 56px clamp(28px, 5vw, 56px) 44px;
`;

const ModalEyebrow = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: ${theme.textMuted};
  margin-bottom: 14px;
`;

const ModalTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 400;
  font-size: clamp(24px, 3vw, 32px);
  letter-spacing: 0.05em;
  color: ${theme.textPrimary};
  margin-bottom: 32px;
`;

const ModalText = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.75;
  color: ${theme.textSecondary};

  p { margin-bottom: 14px; }

  strong {
    color: ${theme.textPrimary};
    font-weight: 500;
    letter-spacing: 0.02em;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 10px 0 22px;
  }
  ul li {
    padding: 6px 0 6px 22px;
    position: relative;
  }
  ul.dots li::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 15px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${theme.hover};
  }
  ul.arrows li::before {
    content: '▸';
    position: absolute;
    left: 0;
    top: 6px;
    color: ${theme.hover};
    font-size: 12px;
  }

  .divider {
    height: 1px;
    background: ${theme.border};
    margin: 24px 0;
  }

  .note {
    font-size: 12px;
    color: ${theme.textMuted};
    letter-spacing: 0.02em;
    line-height: 1.6;
    font-style: italic;
  }

  a.mail {
    color: ${theme.textPrimary};
    text-decoration: none;
    border-bottom: 1px solid ${theme.border};
    padding-bottom: 2px;
    transition: color 0.25s ease, border-color 0.25s ease;
  }
  a.mail:hover {
    color: ${theme.hover};
    border-color: ${theme.hover};
  }
`;

const MailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid ${theme.border};
  margin: 22px 0 30px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  letter-spacing: 0.06em;

  svg { color: ${theme.hover}; flex-shrink: 0; }

  a {
    color: ${theme.textPrimary};
    text-decoration: none;
    transition: color 0.25s ease;
  }
  a:hover { color: ${theme.hover}; }
`;

const SubmitButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 18px 24px;
  background: transparent;
  border: 1px solid ${theme.hover};
  color: ${theme.textPrimary};
  text-decoration: none;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: color 0.3s ease, background 0.3s ease, letter-spacing 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${theme.hover};
    transform: translateX(-101%);
    transition: transform 0.5s cubic-bezier(0.7, 0, 0.2, 1);
    z-index: -1;
  }
  &:hover { color: #fff; letter-spacing: 0.35em; }
  &:hover::before { transform: translateX(0); }
`;

/* ---------- SVG icons (minimal, thin) ---------- */
const IconSoundCloud = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 15v-3" />
    <path d="M5 16v-5" />
    <path d="M8 17V9" />
    <path d="M11 17V7" />
    <path d="M14 17V6" />
    <path d="M14 10a4 4 0 0 1 4-1 4 4 0 0 1 4 4 4 4 0 0 1-4 4h-4" />
  </svg>
);

const IconInstagram = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
  </svg>
);

const IconTelegram = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21.5 3.5 2.5 10.8l6 2.2 2.2 6.5 3.4-3.9 5.6 4.2 1.8-16.3z" />
    <path d="M8.5 13l9-6.5-6.5 8.5" />
  </svg>
);

/* ---------- data ---------- */
const SOCIALS = [
  {
    name: 'SoundCloud',
    handle: '/frkrecords',
    href: 'https://soundcloud.com/frkrecords',
    Icon: IconSoundCloud,
  },
  {
    name: 'Instagram',
    handle: '@frkrecords',
    href: 'https://instagram.com/frkrecords',
    Icon: IconInstagram,
  },
  {
    name: 'Telegram',
    handle: '@frkrecords',
    href: 'https://t.me/frkrecords',
    Icon: IconTelegram,
  },
];

const EMAIL = 'artists@frkrecords.art';

/* ---------- app ---------- */
export default function App() {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (open) document.body.classList.add('no-scroll');
    else document.body.classList.remove('no-scroll');
    return () => document.body.classList.remove('no-scroll');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <Shell>
      <GlobalStyle />
      <Ambience />
      <Noise />

      <Header>
        <HeaderLogo href="#top">
          <img src="/LOGO.svg" alt="FRK" />
        </HeaderLogo>
        <HeaderNav aria-label="Социальные сети">
          {SOCIALS.map(({ name, href, Icon }) => (
            <IconLink key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={name}>
              <Icon className="ico" />
            </IconLink>
          ))}
        </HeaderNav>
      </Header>

      <Hero id="top">
        <HeroEyebrow><span>EST. — UNDERGROUND</span></HeroEyebrow>
        <HeroMark>
          <img src="/LOGO.svg" alt="FRK Records" />
        </HeroMark>
        <HeroTitle>
          <em>Electronic Experimental Records</em>
        </HeroTitle>
        <HeroTagline>
          Резидентский лейбл для звука на границе жанров. Мы работаем
          с артистами, чей материал не помещается в форматы — и не должен.
        </HeroTagline>
        <CTAButton onClick={() => setOpen(true)}>
          Присоединиться
          <ArrowUpRight size={16} strokeWidth={1.4} />
        </CTAButton>
      </Hero>

      <Section id="contact">
        <SectionHead>
          <SectionLabel>— каналы связи —</SectionLabel>
          <SectionTitle>Следи за релизами</SectionTitle>
        </SectionHead>
        <SocialsRow>
          {SOCIALS.map(({ name, handle, href, Icon }) => (
            <SocialCard key={name} href={href} target="_blank" rel="noopener noreferrer">
              <Icon className="glyph" />
              <div className="name">{name}</div>
              <div className="handle">{handle}</div>
              <ArrowUpRight className="arrow" size={16} strokeWidth={1.2} />
            </SocialCard>
          ))}
        </SocialsRow>
      </Section>

      <Footer>
        <div className="copy">© FRK Records</div>
        <HeaderNav aria-label="Социальные сети — футер">
          {SOCIALS.map(({ name, href, Icon }) => (
            <IconLink key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={name}>
              <Icon className="ico" />
            </IconLink>
          ))}
        </HeaderNav>
      </Footer>

      {open && (
        <Overlay onClick={close} role="dialog" aria-modal="true" aria-labelledby="joinTitle">
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalClose onClick={close} aria-label="Закрыть">
              <X size={16} strokeWidth={1.4} />
            </ModalClose>
            <ModalBody>
              <ModalEyebrow>Заявка — A&amp;R</ModalEyebrow>
              <ModalTitle id="joinTitle">ХОЧЕШЬ К НАМ?</ModalTitle>
              <ModalText>
                <p>Чтобы стать частью <strong>FRK</strong>, отправь нам:</p>
                <ul className="dots">
                  <li>Демо или полный трек (ссылкой или файлом)</li>
                  <li>Рассказ о себе и своих планах</li>
                  <li>Почему мы должны выбрать именно тебя?</li>
                </ul>

                <p><strong>Что мы предлагаем:</strong></p>
                <ul className="arrows">
                  <li>Продвижение твоего релиза</li>
                  <li>Создание обложки (по желанию)</li>
                  <li>Публикация трека на всех платформах</li>
                </ul>

                <div className="divider" />

                <p>
                  Если мы заинтересуемся — напишем тебе ответное письмо с условиями
                  сотрудничества. После согласия — пришлём ссылку на контракт.
                </p>

                <p className="note">
                  Важно: ты можешь разорвать контракт в любой момент. Мы не держим артистов.
                </p>

                <MailRow>
                  <Mail size={16} strokeWidth={1.4} />
                  <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                </MailRow>

                <SubmitButton href={`mailto:${EMAIL}?subject=FRK%20—%20Заявка%20артиста`}>
                  Отправить заявку
                  <ArrowUpRight size={14} strokeWidth={1.6} />
                </SubmitButton>
              </ModalText>
            </ModalBody>
          </Modal>
        </Overlay>
      )}
    </Shell>
  );
}
